
import { formatDate } from '../../../libs/date';
import { logoutAccount } from '../../../libs/state';
import { parseError } from '../../../libs/parser';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CardComponent from '../../../components/CardComponent';
import CommonIconButton from '../../../components/CommonIconButton';
import Context from '../../../provider';
import CustomerSelectComponent from '../../../components/CustomerSelectComponent';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import IconBenefitsWhite from '../../../assets/hkdc/icons/BenefitsWhiteIcon';
import IconEdit from '../../../assets/hkdc/icons/EditIcon';
import IconJoin from '../../../assets/hkdc/icons/JoinIcon';
import IconPresent from '../../../assets/hkdc/icons/PresentIcon';
import IconRenewPayment from '../../../assets/hkdc/icons/RenewPaymentIcon';
import IconVoucher from '../../../assets/hkdc/icons/VoucherIcon';
import IconVoucherNone from '../../../assets/hkdc/icons/VoucherNoneIcon';
import IconWebLoginJoin from '../../../assets/hkdc/icons/WebLoginJoinIcon';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LogoFooterComponent from '../../../components/LogoFooterComponent';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import QRCode from 'react-qr-code';
import React, { useContext, useEffect, useRef, useState } from 'react';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

const sortOptions = [
  { label: 'default', value: 'default' },
  { label: 'last-updated', value: 'newest_to_oldest' }
];

export default function WebHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const notificationNotReadCount = useNotificationsCount();

  const { init, me, dispatch, lang, tierMission } = useContext(Context);

  const scrollRef = useRef(null);
  const isScrolling = useIsScrolling(scrollRef.current);

  const [isLogin, setIsLogin] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [newsData, setNewsData] = useState([]);
  const [newsPageTotalCount, setNewsPageTotalCount] = useState(1);
  const [currentNewsPage, setCurrentNewsPage] = useState(1);
  const [newsSortOptions, setNewsSortOptions] = useState([]);
  const [newsSortType, setNewsSortType] = useState('default');

  const [activeTierPageCount, setActiveTierPageCount] = useState(1);
  const [tierBenefitsData, setTierBenefitsData] = useState([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [promotionsData, setPromotionsData] = useState([]);
  const [isClickChangePromotion, setIsClickChangePromotion] = useState(false);
  const autoSwitchRef = useRef(null);

  const [vouchersData, setVouchersData] = useState([]);

  const [currentMissionPage, setCurrentMissionPage] = useState(1);
  const [missionPageTotalCount, setMissionPageTotalCount] = useState(1);
  const [missionsData, setMissionsData] = useState([]);

  const fetchNewsData = async (page) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getNews({
        page,
        pageSize: 1000,
        sort: newsSortType
      });
      setNewsPageTotalCount(Math.ceil(response.list.length / 6));
      setNewsData((prevData) => [...prevData, ...(response?.list ?? []).filter((d) => !(prevData.find((p) => p.uri_name === d.uri_name)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchTierBenefitsData = async (page) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getTierBenefits({
        page,
        pageSize: 1000
      });
      setTierBenefitsData((prevData) => [...prevData, ...(response?.list ?? []).filter((d) => !(prevData.find((p) => p.uri_name === d.uri_name)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchPromotionsData = async (page) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getPromotions({
        page,
        pageSize: 1000
      });
      setPromotionsData((prevData) => [...prevData, ...(response?.list ?? []).filter((d) => !(prevData.find((p) => p.uri_name === d.uri_name)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const getItem = async (page) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getItems({
        page,
        pageSize: 1000,
        status: 'available'
      });
      setVouchersData((prevData) => [...prevData, ...(response?.list ?? []).filter((i) => !(prevData.find((p) => p.id === i.id)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const getMissions = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getMissions({});
      setMissionPageTotalCount(Math.ceil(response.total / 3));
      setMissionsData((prevData) => [...prevData, ...(response?.list ?? []).filter((i) => !(prevData.find((p) => p.id === i.id)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchPageData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: 'join-membership-program' });
      console.log("fetchPagehData", page);

      setPageData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  useEffect(() => {
    setNewsSortOptions(sortOptions);

    return () => {
      setNewsSortOptions([]);
    };
  }, []);

  useEffect(() => {
    setNewsData([]);
    fetchNewsData(1);
    setCurrentNewsPage(1);
  }, [newsSortType]);

  useEffect(() => {
    autoSwitchRef.current = promotionsData.length;
  }, [promotionsData]);

  useEffect(() => {
    let timer;

    if (!isClickChangePromotion) {
      timer = setInterval(() => {
        setActiveBannerIndex((prevIndex) => {
          if (prevIndex === autoSwitchRef.current - 1) {
            return 0;
          } else {
            return prevIndex + 1;
          }
        });
      }, 3000);
    }

    return () => {
      clearInterval(timer);
    };
  }, [isClickChangePromotion]);

  const resetAutoPlay = () => {
    setTimeout(() => {
        setIsClickChangePromotion(false);
    }, 1000);
  };

  useEffect(() => {
    if (scrollRef.current) {
      const homeBarHeight = document.querySelector('.home-bar').clientHeight;

      let timer = null;
      timer = setTimeout(() => {
        let toElementPos = 0;
        if (location.state && location.state?.toClass) {
          toElementPos = scrollRef.current.querySelector(location.state?.toClass)?.offsetTop;
        }

        scrollRef.current?.scrollTo(0, parseInt(toElementPos - homeBarHeight, 10));
        if (timer) {
          clearTimeout(timer);
        }
      }, 1200);
    }
  }, [scrollRef]);

  useEffect(() => {
    if (init) {
      console.log('url location', location);

      fetchNewsData(1);
      fetchTierBenefitsData(1);
      fetchPageData();
      if (!me) {
        fetchPromotionsData(1);
      } else {
        if (me?.status === 'new') {
          logoutAccount({ dispatch });
        }
        if (me?.status === 'activated') {

          setIsLogin(true);
          getItem(1);
          getMissions();
        }
        if (me?.status === 'disabled') navigate(`${config.basename}no-permission`);
      }

    }
  }, [init, me, navigate]);

  return (
    (
      <div className='web-home-page gray-block'>

        <div
          ref={scrollRef}
          onScroll={(e) => {
            const homeBarHeight = document.querySelector('.home-bar').clientHeight;
            let timer = null;
            timer = setTimeout(() => {
              if (me?.status === 'activated') {
                const coupon = scrollRef.current.querySelector('.vouchers-section');
                dispatch({ type: 'SET_NAV_DATA', payload: {
                  isCoupons: e.target.scrollTop > coupon.offsetTop - homeBarHeight - 50 && e.target.scrollTop < coupon.offsetTop + coupon.clientHeight - homeBarHeight
                } });
              } else {
                const tier = scrollRef.current.querySelector('.benefits-section-cont-bg');
                dispatch({ type: 'SET_NAV_DATA', payload: {
                  isTiers: e.target.scrollTop > tier.offsetTop - homeBarHeight - 50 && e.target.scrollTop < tier.offsetTop + tier.clientHeight - homeBarHeight
                } });
              }
              if (timer) {
                clearTimeout(timer);
              }
            }, 300);
          }}
          className='y-overflow content-container'>

          {!isLogin &&
            <>
              <div className="red-block full-width">
                <div className='section-max-container join-section-cont'>
                  <div className='join-text-cont'>
                    <div className="join-text">
                      <div className='join-text-title'>
                        {pageData && pageData.title[lang]}
                      </div>
                      <div className='join-text-desc'>
                        {pageData && pageData.meta_description[lang].split('\r\n')
                          .map((item, index) => {
                            if (index > 0) {
                              return (
                                <>
                                  <br/>
                                  {item}
                                </>
                              );
                            } else {
                              return item;
                            }
                          })
                        }
                      </div>
                    </div>
                    <CommonIconButton
                      text={t('home-before.join-text')}
                      endIcon= {<KeyboardArrowRightIcon />}
                      alignItems = "unset"
                      btnBackgroundColor= "#004899"
                      btnColor = "#ffffff"
                      btnWidth= "max-content"
                      btnPadding = "0.75rem 1.25rem 0.75rem 2rem"
                      btnFontSize = "1rem"
                      btnFontWeight = "700"
                      onClick={() => {
                        setTimeout(() => {
                          navigate(`${config.basename}${"join-membership-program"}`, {
                            state: {
                              defaultTierId: tierBenefitsData && tierBenefitsData.length > 0 ? tierBenefitsData[0].id : -1,
                              defaultTierType: tierBenefitsData && tierBenefitsData.length > 0 ? tierBenefitsData[0].uri_name.replace('/tier-', '') : ''
                            }
                          });
                        }, 200);
                      }}
                    />
                  </div>
                  <div className='mobile-join-icon-cont'>
                    <IconJoin
                      className="join-icon"
                      size="large" />
                  </div>
                </div>
              </div>
            </>
          }

          {!isLogin &&
            <>
              {
                promotionsData && promotionsData.length > 0 &&
                <>
                  <div className='block-cont' >
                    <div className='block'/>
                  </div>
                  <div className='section-max-container section-2-cont'>
                    <div className='banner'>
                      <div className='warp'>
                        <div className='item'>
                          <div className='red-block block' />
                          <div className='img-cont'
                            onClick={(e) => {
                              e.stopPropagation();
                              if (promotionsData[activeBannerIndex].external_url) {
                                window.open(promotionsData[activeBannerIndex].external_url[lang]);
                              }
                            }}
                            style={{
                              backgroundImage: `url(${promotionsData[activeBannerIndex] && promotionsData[activeBannerIndex].cover ? promotionsData[activeBannerIndex].cover[lang] : ''})`,
                              cursor: promotionsData[activeBannerIndex].external_url ? 'pointer' : 'default'
                            }}
                          />
                        </div>
                      </div>
                      <div className='page-nation'>
                        <div className='blue-block block' />
                        <div className="nation-btn">
                          <div className='nation-btn-previous'
                            style={{
                              cursor: activeBannerIndex !== 0 ? 'pointer' : 'default',
                              pointerEvents: activeBannerIndex !== 0 ? 'auto' : 'none'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeBannerIndex !== 0) {
                                setIsClickChangePromotion(true);
                                setActiveBannerIndex(activeBannerIndex - 1);
                                resetAutoPlay();
                              }
                            }}
                          >
                            <KeyboardArrowLeftIcon
                              className={`btn-icon`}
                              style={{
                                visibility: activeBannerIndex !== 0 ? 'visible' : 'hidden'
                              }}
                              size="large"
                            />
                          </div>
                          <div className='nation-btn-next'
                            style={{
                              cursor: activeBannerIndex < promotionsData.length - 1 ? 'pointer' : 'default',
                              pointerEvents: activeBannerIndex < promotionsData.length - 1 ? 'auto' : 'none'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('activeBannerIndex', activeBannerIndex);

                              if (activeBannerIndex < promotionsData.length - 1) {
                                setIsClickChangePromotion(true);
                                setActiveBannerIndex(activeBannerIndex + 1);
                                resetAutoPlay();
                              }
                            }}
                          >
                            <KeyboardArrowRightIcon
                              className={`btn-icon`}
                              style={{
                                visibility: activeBannerIndex < promotionsData.length - 1 ? 'visible' : 'hidden'
                              }}
                              size="large"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>

              }

              <div className='block-cont' >
                <div className='block'/>
              </div>

              <div id='tiers' className="section-max-container benefits-section-cont-bg">
                <div className='benefits-section-cont'>
                    <div className='benefits-title-cont'>
                      <IconBenefitsRed
                        className="icon"
                        size="large" />
                      <div className='title'>
                        {t('common.tier-benefits')}
                      </div>
                    </div>
                    <div className='benefits-cont'>
                      {
                        tierBenefitsData && tierBenefitsData.length > 0 &&
                        tierBenefitsData
                          .filter((item, index) => (index + 1) - activeTierPageCount < 3 && (index + 1) - activeTierPageCount >= 0)
                          .map((item, index) => {
                            const tierType = item.uri_name.replace('/tier-', '');
                            return (
                              <CardComponent
                                key={item.uri_name}
                                cardBg={item.cover[lang]}
                                cardTitle={item.title[lang]}
                                cardDesc={item.subtitle[lang]}
                                cardImgClass={'tier-card-img'}
                                contentClass={'tier-card-content'}
                                btn={
                                  <CommonIconButton
                                    text= {t('home-before.card-btn-3')}
                                    alignItems = "unset"
                                    backgroundColor= "#000000"
                                    btnBackgroundColor= "#000000"
                                    btnColor = "#ffffff"
                                    width= "max-content"
                                    padding = "0.5rem 0.75rem"
                                    btnPadding="0rem"
                                    btnFontSize = "1rem"
                                    btnFontWeight = "700"
                                    onClick={(e) => {
                                      navigate(`${config.basename}benefit/${tierType}`);
                                    }}
                                  />
                                }
                              />
                            );
                          }
                        )
                      }
                    </div>
                </div>
                <div className="benefits-nation">
                  <div className="nation-btn">
                      <div className='nation-btn-previous'
                        style={{
                          cursor: activeTierPageCount !== 1 ? 'pointer' : 'default',
                          pointerEvents: activeTierPageCount !== 1 ? 'auto' : 'none'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeTierPageCount !== 1) {
                            setActiveTierPageCount(activeTierPageCount - 1);
                          }
                        }}
                      >
                        <KeyboardArrowLeftIcon
                          className="btn-icon"
                          style={{
                            visibility: activeTierPageCount !== 1 ? 'visible' : 'hidden'
                          }}
                          size="large"
                        />
                      </div>
                      <div className='nation-btn-next'
                        style={{
                          cursor: activeTierPageCount + 2 < tierBenefitsData.length ? 'pointer' : 'default',
                          pointerEvents: activeTierPageCount + 2 < tierBenefitsData.length ? 'auto' : 'none'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeTierPageCount + 2 < tierBenefitsData.length) {
                            setActiveTierPageCount(activeTierPageCount + 1);
                          }
                        }}
                      >
                        <KeyboardArrowRightIcon
                          className="btn-icon"
                          style={{
                            visibility: activeTierPageCount + 2 < tierBenefitsData.length ? 'visible' : 'hidden'
                          }}
                          size="large"
                        />
                      </div>
                    </div>
                </div>
              </div>
            </>
          }

          { isLogin &&
            <>
              <div className="red-block full-width">
                <div className="section-max-container user-card-section-cont">
                  <div className="user-card-cont">
                    <div className="user-card-text">
                        <IconBenefitsWhite
                          className="icon"
                          size="large" />
                        <div className="text-cont">
                          <div className="welcome-text">
                            {t('home-after.welcome')}
                          </div>
                          {
                            me && me.loyalty_account &&
                            <div className="user-name-text">
                              {`${me.loyalty_account.first_name} ${me.loyalty_account.last_name}`}
                            </div>
                          }
                        </div>
                    </div>
                    <div className="user-card"
                      style={{ backgroundImage: `url(${tierMission?.current_tier?.image?.[lang]})` }}
                    >
                      {
                        me && me.loyalty_account?.unique_id &&
                        <QRCode
                          className='qrcode'
                          size={256}
                          value={me?.loyalty_account?.unique_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({ type: 'SWITCH_USER_INFO', payload: { open: true } });
                          }}
                        />
                      }
                      <div className="func-cont">
                        <div className="view-benefits-cont">
                          <div style={{ color: tierMission?.current_tier?.color ? tierMission?.current_tier?.color : undefined }} className="title">
                            {tierMission?.current_tier?.title?.[lang]}
                          </div>
                          <div className="view-btn" onClick={() => {
                            let item = tierBenefitsData.find((item) => item.uri_name.includes(tierMission?.current_tier?.code));
                            if (!item) {
                              item = tierBenefitsData.find((item) => item.uri_name.includes('patron'));
                            }
                            const tierType = item.uri_name.replace('/tier-', '');
                            console.log('view-btn', tierType);
                            navigate(`${config.basename}benefit/${tierType}`);
                          }}>
                            <div className="text-cont-underline">
                              <div style={{ color: tierMission?.current_tier?.color ? tierMission?.current_tier?.color : undefined }} className="text">
                                {t('home-after.card-btn')}
                              </div>
                              <div style={{ borderColor: tierMission?.current_tier?.color ? tierMission?.current_tier?.color : undefined }} className="middle-line"/>
                            </div>
                            <KeyboardArrowRightIcon style={{ color: tierMission?.current_tier?.color ? tierMission?.current_tier?.color : undefined }} />
                          </div>
                        </div>
                        <Button
                          style={{
                            backgroundColor: tierMission?.current_tier?.color ? `${tierMission?.current_tier?.color}64` : undefined
                          }}
                          variant='contained'
                        >
                          {`${t('home-after.coupon-expiry-1')}${tierMission?.expire_at ? formatDate(tierMission?.expire_at, lang) : ' -'}`}
                        </Button>
                      </div>
                    </div>
                    <div className="func-btn-container">
                      <div className="edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${config.basename}setting/profile/user`);
                        }}
                      >
                        <IconEdit
                          className="icon"
                          size="large" />
                        <div className="text">
                          {t('home-after.btn')}
                        </div>
                      </div>

                      {
                        me && me.loyalty_account?.tier_renewal && tierMission?.expire_at &&
                        me.loyalty_account?.tier_renewal.expire_from_now <= 30 &&
                        <div className="edit-btn"
                          style={{ backgroundColor: '#004899' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${config.basename}renewal`);
                          }}
                        >
                          <IconRenewPayment
                            className="icon"
                            size="large" />
                          <div className="text">
                            {t('home-after.shortcut-3')}
                          </div>
                        </div>
                      }
                    </div>

                  </div>
                  <IconWebLoginJoin
                      className="join-icon"
                      size="large" />
                </div>
              </div>

              <div className='block-cont' >
                <div className='block'/>
              </div>

              <div id='coupons' className="section-max-container vouchers-section">
                <div className="vouchers-cont">
                  <div className="bar-title-container">
                    <IconBenefitsRed
                      className="icon"
                      size="large" />
                    <div className="title" style={{ width: '20.875rem' }}>
                      {t('home-after.title-1')}
                    </div>
                  </div>
                  <div className="content-list-cont">
                  {
                    vouchersData.length > 0 && vouchersData.map((item, index) => {
                      return (
                        <div key={index} className="content-item-cont">
                          <div className="item-icon-title">
                            <div className="icon-cont">
                              <IconVoucher
                                className="icon"
                                size="large" />
                            </div>
                            <div className="title-cont">
                              <div className="title">
                                {item.title[lang]}
                              </div>
                              <div className="time">
                                {`${t('home-after.coupon-expiry-1')}${formatDate(item.expire_at, lang)}`}
                              </div>
                            </div>
                          </div>
                          <div className="item-btn-cont">
                            <div className="btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`${config.basename}voucher/${item.token}`, {
                                  state: {
                                    data: item.token
                                  }
                                });
                              }}
                            >
                              <div className="text">
                                USE NOW
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                  {
                    (!vouchersData || vouchersData.length === 0) &&
                    <div className="voucher-list-none">
                      <IconVoucherNone
                        className="icon"
                        size="large" />
                      <div className="text">
                        {t('none-data.coupon')}
                      </div>
                    </div>
                  }
                  </div>
                </div>
              </div>

              <div className='block-cont' >
                <div className='block'/>
              </div>

              {
                missionsData.length > 0 &&
                <>
                  <div className="section-max-container mission-section">
                    <div className="mission-cont">
                      <div className="mission-content-cont">
                        <div className="bar-title-container">
                          <IconBenefitsRed
                            className="icon"
                            size="large" />
                          <div className='title'>
                            {t('home-after.title-2')}
                          </div>
                        </div>
                        <div className="content-cont">
                        {
                          missionsData
                          .filter((item, index) => index >= (currentMissionPage - 1) * 3 && index < currentMissionPage * 3)
                          .map((item, index) => {
                            return (
                              <div key={index} className='item'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`${config.basename}mission/${item.code}`, {
                                    state: {
                                      id: item.code
                                    }
                                  });
                                }}
                              >
                                <div className='item-img' style={{ backgroundImage: `url(${item.image[lang]})` }} />
                                <div className='mission-item-content'>
                                  <div className='item-text-cont'>
                                    <div className='item-text-time'>
                                      {`${t('home-after.mission-expiry-2')}${formatDate(item.expire_at, lang)}`}
                                    </div>
                                    <div className='item-text-desc'>
                                      {item.title[lang]}
                                    </div>
                                  </div>
                                  <div className='schedule-cont'>
                                    <div className="icon-text-cont">
                                      <div className="icon-section">
                                        <IconPresent
                                          className="icon"
                                          size="large" />
                                        {
                                          item.achieve && item.achieve.length > 0 &&
                                          <div className="text">
                                            {`${item.achieve?.[0].quantity} ${item.achieve?.[0].title[lang]}`}
                                          </div>
                                        }
                                      </div>
                                      <div className="text-cont">
                                        <div className="progress">
                                          {item.progress.current}
                                        </div>
                                        <div className="total">
                                          {`/${item.progress.total}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="progress-bar">
                                      <div className="bar" />
                                      <div className="inner" style={{ width: `${item.progress.percentage}%` }} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        }
                        </div>
                      </div>
                      <div className="page-count">
                        <Pagination
                          count={missionPageTotalCount}
                          variant="outlined"
                          shape="rounded"
                          value={currentMissionPage}
                          onChange={(event, value) => {
                            console.log(value);
                            setCurrentMissionPage(value);
                          }}
                          renderItem={(item) => {
                            return (
                              <PaginationItem
                              sx={{
                                borderRadius: '0',
                                border: '0.125rem solid #A1A3A5',
                                minWidth: '2.8125rem',
                                height: '2.8125rem',
                                padding: '0.25rem',
                                color: '#A1A3A5',
                                fontWeight: '700',
                                fontSize: '1rem',
                                '&.Mui-selected': {
                                  border: '0.125rem solid #004899',
                                  backgroundColor: '#004899',
                                  color: 'white'
                                }
                              }}
                                {...item}
                              />
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='block-cont' >
                    <div className='block'/>
                  </div>
                </>
              }
            </>
          }

          {
            newsData && newsData.length > 0 &&
            <>
              {
                !isLogin &&
                <div className='block-cont' >
                  <div className='block'/>
                </div>
              }

              <div className="section-max-container news-section-cont-bg">
                <div className="news-section-cont">
                  <div className="news-external-cont">
                    <div className="news-bar">
                      <div className="bar-title-container">
                        <IconBenefitsRed
                        className="icon"
                        size="large" />
                        <div className='title'>
                          {t('common.news')}
                        </div>
                      </div>
                      <CustomerSelectComponent
                        title={t('news.sort')}
                        value={newsSortType}
                        options={newsSortOptions}
                        selectChange={(value) => {
                          setNewsSortType(value);
                        }}
                      />
                    </div>
                    <div className="news-cont">
                      {
                        newsData && newsData.length > 0 &&
                        newsData
                        .filter((item, index) => index >= (currentNewsPage - 1) * 6 && index < currentNewsPage * 6 )
                        .map((news, index) => {
                          return (
                            <CardComponent
                              key={index}
                              cardBg={news.cover[lang]}
                              cardTitle={news.title[lang]}
                              cardDesc={news.subtitle[lang]}
                              cardStyle={{
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                              cardImgClass={'card-img'}
                              contentClass={'news-card-content'}
                              btn={
                                <CommonIconButton
                                  text= {t('home-before.card-btn-4')}
                                  alignItems = "unset"
                                  backgroundColor= "#000000"
                                  btnBackgroundColor= "#000000"
                                  btnColor = "#ffffff"
                                  width= "max-content"
                                  padding = "0.5rem 0.75rem"
                                  btnPadding="0rem"
                                  btnFontSize = "1rem"
                                  btnFontWeight = "700"
                                  onClick={(e) => {
                                    if (news.external_url && news.external_url[lang]) {
                                      window.location.assign(news.external_url[lang]);
                                    } else {
                                      navigate(`${config.basename}news-detail/${news.id}`, {
                                        state: {
                                          id: news.id
                                        }
                                      });
                                    }
                                  }}
                                />
                              }
                            />
                          );
                        })
                      }
                    </div>
                    <div className="page-count">
                      <Pagination
                        count={newsPageTotalCount}
                        variant="outlined"
                        shape="rounded"
                        value={currentNewsPage}
                        onChange={(event, value) => {
                          setCurrentNewsPage(value);
                        }}
                        renderItem={(item) => {
                          return (
                            <PaginationItem
                            sx={{
                              borderRadius: '0',
                              border: '0.125rem solid #A1A3A5',
                              minWidth: '2.8125rem',
                              height: '2.8125rem',
                              padding: '0.25rem',
                              color: '#A1A3A5',
                              fontWeight: '700',
                              fontSize: '1rem',
                              '&.Mui-selected': {
                                border: '0.125rem solid #004899',
                                backgroundColor: '#004899',
                                color: 'white'
                              }
                            }}
                              {...item}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>

          }

          <div className='block-cont' >
            <div className='block'/>
          </div>

          <div className="black-block full-width">
            <LogoFooterComponent />
          </div>
        </div>
      </div>
    )
  );
}